import { format, isValid, parse } from 'date-fns';
import type { DayRange } from '@/lib/automation-replies';
import type { DeviationSentVia, DeviationSort } from '@/services/deviation.service';

/**
 * The Logs › Deviations filters as URL parameters, so a filtered view is a link that can be shared
 * (`?tab=deviations&sentVia=extension&sort=editRate.desc`). Reading validates every value: anything a
 * hand-edited link gets wrong reads as "not set" rather than reaching the API. Intents and senders
 * are comma lists.
 */

/** Reviewed rows only, or not reviewed yet; unset = both. */
export type DeviationReviewedFilter = 'yes' | 'no';

export interface DeviationUrlState {
	q?: string;
	days: DayRange;
	sentVia?: DeviationSentVia;
	reviewed?: DeviationReviewedFilter;
	intents: string[];
	senders: string[];
	editRateMin?: number;
	editRateMax?: number;
	sort: DeviationSort;
}

export type DeviationUrlPatch = Partial<Omit<DeviationUrlState, 'days'>> & {
	days?: DayRange;
};

const SORTS: readonly DeviationSort[] = ['recent', 'editRate.desc', 'editRate.asc'];
export const SENT_VIA_VALUES: readonly DeviationSentVia[] = ['workspace', 'extension'];
export const REVIEWED_VALUES: readonly DeviationReviewedFilter[] = ['yes', 'no'];
const DAY_FORMAT = 'yyyy-MM-dd';
const EDIT_RATE_PARAMS = ['editRateMin', 'editRateMax'] as const;

/** A whole percent 0–100 from a URL value or an input; undefined otherwise. */
export function parseEditRate(value: string | null | undefined): number | undefined {
	if (value == null || !/^\d{1,3}$/.test(value.trim())) return undefined;
	const percent = Number(value.trim());
	return percent <= 100 ? percent : undefined;
}

const parseDay = (value: string | null): Date | undefined => {
	if (!value) return undefined;
	const day = parse(value, DAY_FORMAT, new Date());
	return isValid(day) && format(day, DAY_FORMAT) === value ? day : undefined;
};

const textOrUndefined = (value: string | null): string | undefined => value?.trim() || undefined;

/** One of the known values, or undefined. */
const oneOf = <T extends string>(known: readonly T[], value: string | null): T | undefined =>
	known.find((candidate) => candidate === value?.trim());

/** A comma list from the URL, trimmed, without blanks or repeats. */
const readList = (value: string | null): string[] => [
	...new Set(
		(value ?? '')
			.split(',')
			.map((item) => item.trim())
			.filter(Boolean)
	)
];

export function readDeviationParams(params: URLSearchParams): DeviationUrlState {
	let editRateMin = parseEditRate(params.get('editRateMin'));
	let editRateMax = parseEditRate(params.get('editRateMax'));
	// A range that cannot match anything reads as no range.
	if (editRateMin != null && editRateMax != null && editRateMin > editRateMax) editRateMin = editRateMax = undefined;
	return {
		q: textOrUndefined(params.get('q')),
		days: {
			startDate: parseDay(params.get('from')),
			endDate: parseDay(params.get('to'))
		},
		sentVia: oneOf(SENT_VIA_VALUES, params.get('sentVia')),
		reviewed: oneOf(REVIEWED_VALUES, params.get('reviewed')),
		intents: readList(params.get('intent')),
		senders: readList(params.get('sender')),
		editRateMin,
		editRateMax,
		sort: oneOf(SORTS, params.get('sort')) ?? 'recent'
	};
}

const setOrDelete = (params: URLSearchParams, key: string, value: string | number | undefined): void => {
	if (value === undefined || value === '') params.delete(key);
	else params.set(key, String(value));
};

/** A copy of `params` with the patched filters written; unset values are removed, other parameters kept. */
export function writeDeviationParams(params: URLSearchParams, patch: DeviationUrlPatch): URLSearchParams {
	const next = new URLSearchParams(params);
	if ('q' in patch) setOrDelete(next, 'q', patch.q?.trim());
	if ('sentVia' in patch) setOrDelete(next, 'sentVia', patch.sentVia);
	if ('reviewed' in patch) setOrDelete(next, 'reviewed', patch.reviewed);
	if (patch.intents) setOrDelete(next, 'intent', patch.intents.join(','));
	if (patch.senders) setOrDelete(next, 'sender', patch.senders.join(','));
	for (const key of EDIT_RATE_PARAMS) if (key in patch) setOrDelete(next, key, patch[key]);
	if ('sort' in patch) setOrDelete(next, 'sort', patch.sort === 'recent' ? undefined : patch.sort);
	if (patch.days) {
		setOrDelete(next, 'from', patch.days.startDate && format(patch.days.startDate, DAY_FORMAT));
		setOrDelete(next, 'to', patch.days.endDate && format(patch.days.endDate, DAY_FORMAT));
	}
	return next;
}

/** The order after a click on the Edit % header: high to low, low to high, then back to newest first. */
export function nextEditRateSort(sort: DeviationSort): DeviationSort {
	if (sort === 'recent') return 'editRate.desc';
	if (sort === 'editRate.desc') return 'editRate.asc';
	return 'recent';
}
